<template>
  <div id="richTextSection" class="main-area" :class="{ disabled: props.disabled }">
    <div class="editor-container">
      <div class="template-button-wrapper">
        <AppButton v-if="props.isHasContent" sm secondary title="Açıklama Şablonu Seç" :disabled="props.disabled" @click="handleClick" />
      </div>
      <JoditEditor
        v-model="internalContent"
        :readOnly="props.disabled"
        :disabled="props.disabled"
        :placeholder="hasContent ? '' : 'Ürünün satışını artırmak için ürün özelliklerini madde madde açıklayabilir, avantajlarını vurgulayabilirsiniz.'"
      />
    </div>

    <div v-if="hasContent" class="info-container" :class="{ disabled: props.disabled }">
      <Icons name="InfoCircle" :color="props.disabled ? '#B4B4B4' : '#4FA4E5'" lg class="info-icon" />
      <p>Ürünün satışını artırmak için ürün özelliklerini madde madde açıklayabilir, avantajlarını vurgulayabilirsiniz.</p>
    </div>
    
    <span v-if="!isDescriptionAreaValid" class="errorText">Açıklama alanı boş olamaz.</span>
    <div class="form-group">
      <div class="checkbox-container">
        <div class="checkbox-label" :class="{ disabled: props.disabled }">
          <Checkbox
            label="Bu açıklamayı daha sonra tekrar kullanmak için şablon olarak kaydet."
            id="select"
            :checked="isReusablePattern"
            :disabled="props.disabled"
            :onClickFn="handleCheckBoxClick"
          />
        </div>
        <span v-if="isTemplateContentError" class="errorText">Şablon oluşturmak için açıklama girin</span>
      </div>

      <div v-if="isReusablePattern" class="input-field">
        <Input
          type="text"
          class="custom-input"
          id="template-name"
          v-model:value="templateName"
          placeholder="Şablon Adı Gir"
          :isError="isError"
          :disabled="props.disabled"
        />
        <span v-if="isError" class="errorText">Şablon adı boş bırakılamaz.</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { Checkbox, AppButton, Input, Icons, JoditEditor } from "@n11/so-ui-components";
import { ref, watch, computed } from "vue";
import "jodit/build/jodit.min.css";

const emit = defineEmits(['open-modal','update:content','update:isReusablePattern','update:templateName']);

const props = defineProps({
  content: {
    type: String,
    required: false,
    default: ""
  },
  isHasContent: {
    type: Boolean,
    default: false
  },
  templateName: {
    type: String,
    default: ""
  },
  isReusablePattern: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  }
});

const isError = ref(false);
const isReusablePattern = ref(props.isReusablePattern);
const internalContent = ref(props.content || null);
const templateName = ref(props.templateName || "");
const isDescriptionAreaValid = ref(true);
const isReusablePatternValid = ref(false);
const isTemplateContentError = ref(false);

const isEmptyContent = (val) => {
  if (!val) return true;
  const trimmed = val.trim();
  return trimmed === "" || trimmed === "<p><br></p>" || trimmed === "<p></p>" || trimmed === "<br>";
};

const hasContent = computed(() => {
  if (!internalContent.value) return false;
  return !isEmptyContent(internalContent.value);
});

watch(() => props.content, (newVal) => {
  internalContent.value = newVal;
});

watch(() => props.templateName, (newVal) => {
  templateName.value = newVal || "";
});

watch(() => props.isReusablePattern, (newVal) => {
  isReusablePattern.value = newVal;
});

watch(internalContent, (val) => {
  const isEmpty = isEmptyContent(val);
  const finalVal = isEmpty ? null : val;
  isDescriptionAreaValid.value = !isEmpty;
  
  if (!isEmpty && isTemplateContentError.value) {
    isTemplateContentError.value = false;
  }

  emit('update:content', finalVal);
});

watch(templateName, (val) => {
  emit("update:templateName", val);
  
  if (val.trim() !== "") {
    isError.value = false;
  }
});

const handleClick = () => {
  emit('open-modal');
}

const handleCheckBoxClick = () => {
  if (props.disabled) {
    return;
  }
  
  const willBeChecked = !isReusablePattern.value;
  
  if (willBeChecked) {
    if (isEmptyContent(internalContent.value)) {
      isTemplateContentError.value = true;
      return;
    }
  }
  
  isTemplateContentError.value = false;
  isReusablePattern.value = willBeChecked;
  emit('update:isReusablePattern', isReusablePattern.value);
}

const getRichText = () => {
  const cleaned = (internalContent.value || "").replace(/<[^>]*>/g, '').trim();
  isDescriptionAreaValid.value = cleaned !== '';
  isReusablePatternValid.value = isReusablePattern.value ? templateName.value.trim() !== "" : true;
  const validTemplate = isDescriptionAreaValid.value && isReusablePatternValid.value;
  isError.value = !isReusablePatternValid.value;

  return {
    data: {
      templateName: validTemplate ? templateName.value : null,
    },
    isValid: validTemplate,
  };
};

const validateTemplateName = () => {
  if (isReusablePattern.value && (!templateName.value || templateName.value.trim() === '')) {
    isError.value = true;
    return false;
  }
  isError.value = false;
  return true;
};

const resetTemplate = () => {
  templateName.value = "";
  isReusablePattern.value = false;
  isError.value = false;
  isTemplateContentError.value = false;
  emit('update:isReusablePattern', false);
};

defineExpose({
  getRichText,
  validateTemplateName,
  resetTemplate
})

</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/utilities.scss";

.main-area {
  border-radius: 8px;
  height: auto;
  position: relative;
  
  &.disabled {
    opacity: 0.9;
    pointer-events: none;
  }
}

.editor-container {
  position: relative;

  .template-button-wrapper {
    position: absolute;
    top: 8px;
    right: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    padding: 6px 8px;

    button {
      white-space: nowrap;
      padding: 7px 16px;
      line-height: 16px;
    }
  }
}

.form-group {
  display: flex;
  align-items: center;
  gap: 24px;
  margin-top: 16px;

  .checkbox-container {
    display: flex;
    flex-direction: column;
  }

  .checkbox-label {
    display: flex;
    align-items: center;
    white-space: nowrap;
    
    &.disabled {
      cursor: not-allowed;
      pointer-events: none;
      
      :deep(*) {
        cursor: not-allowed;
        pointer-events: none;
      }
    }
  }

  .input-field {
    flex: 0 0 306px;
    display: flex;
    flex-direction: column;
  }
  .custom-input {
    padding: 8px;
    border: 1px solid #ccc;
    border-radius: 4px;
  }

  label {
    font-size: 14px;
    color: #333;
  }

  input[type="checkbox"] {
    margin-right: 8px;
  }
}

.errorText {
  font-size: 11px;
  color: $colorE50;
  margin-top: 4px;
}

.info-container {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  .info-icon {
    flex-shrink: 0;
  }
  p {
    color: $colorN80;
    font-size: 13px;
    font-weight: 400;
    line-height: 18px;
    letter-spacing: -0.05px;
  }
}
</style>
